// Form-related constants and pure helper functions
// Extracted from portal.jsx — Phase 3 Batch 1.
// TypeScript source — formConstants.js is a thin re-export stub.

import { C } from "./constants";

// ── STATUS CONFIG ─────────────────────────────────────────────────────────
// The COMPLETE request-status set, keyed for display colour/bg. All statuses are
// system-derived — no user sets them directly. The overall status uses a
// "Processed" vocabulary only (26 June 2026): Draft / Submitted / Part Processed
// / Fully Processed. The old "Approved" / "Declined" / "Completed" request-level
// values were retired (item-level approval still uses Approved/Declined, but that
// is a different concept rendered by ApprovalBadge, not this map).
export const STATUS_CONFIG = {
  Draft: { colour: C.muted, bg: "#F5F5F5" },
  Submitted: { colour: C.blue, bg: "#E3F2FD" },
  "Part Processed": { colour: C.orange, bg: "#FFF3E0" },
  "Fully Processed": { colour: C.green, bg: "#E8F5E9" },
};

// ── YEAR LEVELS & GENDERS ─────────────────────────────────────────────────
export const YEAR_LEVELS = [
  "Prep / Kindy",
  "Year 1",
  "Year 2",
  "Year 3",
  "Year 4",
  "Year 5",
  "Year 6",
  "Year 7",
  "Year 8",
  "Year 9",
  "Year 10",
  "Year 11",
  "Year 12",
];
export const LAPTOP_YEAR_LEVELS = [
  "Prep / Kindy",
  "Year 1",
  "Year 2",
  "Year 3",
  "Year 4",
  "Year 5",
  "Year 6",
  "Year 7",
  "Year 8",
  "Year 9",
  "Year 10",
  "Year 11",
  "Year 12",
  "TAFE",
];
// Map a YEAR_LEVELS label to the numeric BYOD band scale: Prep / Kindy = 0,
// "Year N" = N (1..12). "TAFE" and anything unrecognised → null = not applicable
// (no BYOD). Drives resolving a child's stored school_byod band (db-198/db-199).
export function yearLevelToNumber(label: unknown): number | null {
  if (typeof label !== "string") return null;
  const t = label.trim();
  if (t === "Prep / Kindy") return 0;
  const m = t.match(/^Year (\d{1,2})$/);
  if (!m) return null;
  const n = Number(m[1]);
  return n >= 1 && n <= 12 ? n : null;
}

export const PRIMARY_YEARS = [
  "Prep / Kindy",
  "Year 1",
  "Year 2",
  "Year 3",
  "Year 4",
  "Year 5",
  "Year 6",
];

// School-table "Type" (ACARA) to EXCLUDE from the picker for a given year level, so
// only age-appropriate schools are offered (John, 9 Aug 2026):
//   Prep–Year 6 → hide Secondary-only schools (exclude "Secondary")
//   Year 7–12   → hide Primary-only schools   (exclude "Primary")
//   TAFE / unset → no phase filter (null)
// Combined / blank-type schools are never excluded — they show in both phases.
export function schoolExcludeTypeForYearLevel(yearLevel: string | null | undefined): string | null {
  if (!yearLevel || yearLevel === "TAFE") return null;
  return PRIMARY_YEARS.includes(yearLevel) ? "Secondary" : "Primary";
}

export const GENDERS = ["Male", "Female", "Non-binary / Other", "Prefer not to say"];

// ── STANDARD COSTS DEFAULTS ───────────────────────────────────────────────
export const DEFAULT_STANDARD_COSTS = {
  stationery_primary: 85,
  stationery_secondary: 120,
  laptop: 600,
  ipad: 550,
  joggers_primary: 60,
  joggers_secondary: 80,
  leather_shoes: 100,
};

// ── FEE & DEVICE TYPES ───────────────────────────────────────────────────
export const FEE_TYPES_COMMON = [
  "Stationery",
  "Stationery & Textbooks",
  "Camp Fees",
  "Resource Fees",
  "Device Hire",
  "School Activities",
  "Non-school Activities",
  "Swim Gear",
  "Other (specify)",
];
export const FEE_TYPES_QLD = [
  "Stationery",
  "Textbooks",
  "Camp Fees",
  "Resource Fees",
  "Device Hire",
  "School Activities",
  "Non-school Activities",
  "Swim Gear",
  "Other (specify)",
];
export const FEE_TYPES = FEE_TYPES_COMMON;

// Display label for a fee type. The stored fee_type value stays "Stationery" (the
// warehouse stationery-pack key used across SO rules, product maps and history);
// it is SHOWN to shelters/staff as "Generic (Grade based) Stationery Pack".
export function feeTypeLabel(ft: string): string {
  return ft === "Stationery" ? "Generic (Grade based) Stationery Pack" : ft;
}

// Shelter-side per-fee-type help keys. Powers the "?" bubbles on the shelter
// New Request wizard (ChildFeesSection). These are INDEPENDENT from the
// responder-side scc_* keys (SCC_HELP_KEYS in ChildItemReadView) so the shelter
// and responder wording can differ. Registered + editable in HelpContentAdmin
// (Shelter tab); seeded by docs/db-49-shelter-fee-help-seed.sql.
export const SHELTER_FEE_HELP_KEYS = {
  Stationery: "shelter_scc_stationery",
  Textbooks: "shelter_scc_textbooks",
  "Stationery & Textbooks": "shelter_scc_stationery_textbooks",
  "Camp Fees": "shelter_scc_camp_fees",
  "Resource Fees": "shelter_scc_resource_fees",
  "Device Hire": "shelter_scc_device_hire",
  "School Activities": "shelter_scc_school_activities",
  "Non-school Activities": "shelter_scc_non_school_activities",
  "Swim Gear": "shelter_scc_swim_gear",
  "Other (specify)": "shelter_scc_other",
};
export const DEVICE_TYPES = ["Laptop", "iPad"];
export const ALL_STATUSES = Object.keys(STATUS_CONFIG);

// ── UNIFORM DEFINITIONS ──────────────────────────────────────────────────
// Order is significant: the first half of this array renders as the LEFT
// column in the UniformTable, the second half as the RIGHT column. Keep
// the array length even so the split is clean.
export const UNIFORM_ITEMS = [
  // Left column (top → bottom)
  "Formal Shirt",
  "Formal Shorts",
  "Formal Skirt",
  "Sports/Polo Shirt",
  "Sports Shorts",
  "Sports Skirt/Skort",
  // Right column (top → bottom)
  "Dress",
  "Sun-Safe Everyday Hat",
  "Sports Hat",
  "Tie",
  "Socks",
  "Backpack",
];
export const WINTER_ITEMS = ["Track Jacket", "Track Pants", "Jumper"];

// ── SCHOOL AUTOCOMPLETE EXCEPTIONS ───────────────────────────────────────
export const SCHOOL_EXCEPTIONS = ["Home Schooling", "TAFE", "Not applicable", "Other"];

// ── FORM TABS ────────────────────────────────────────────────────────────
export const TABS = [
  { key: "general", label: "General Info", helpKey: "general_info" },
  { key: "fees", label: "School Costs & Stationery", helpKey: "fees_overview" },
  { key: "uniforms", label: "Uniforms", helpKey: "uniforms" },
  { key: "shoes", label: "Shoes", helpKey: "uniforms_shoes" },
  { key: "laptop", label: "Device", helpKey: "laptop_eligibility" },
  { key: "files", label: "Files", helpKey: "uploads" },
];

// ── Shared local types ───────────────────────────────────────────────────
// Admin-configured standard costs (matches DEFAULT_STANDARD_COSTS).
interface StandardCosts {
  stationery_primary: number;
  stationery_secondary: number;
  laptop: number;
  ipad: number;
  joggers_primary: number;
  joggers_secondary: number;
  leather_shoes: number;
}

// A uniform line item. qty/cost are number | string because they come from
// either form state (strings) or DB-mapped rows (numbers); the math below
// relies on JS coercion, mirrored here with runtime-safe casts.
interface UniformLineItem {
  qty?: number | string;
  cost?: number | string;
  // Present on custom / "Additional Items" rows only.
  name?: string;
  sku?: string;
  size?: string;
}
interface UniformLike {
  items?: Record<string, UniformLineItem>;
  customItems?: UniformLineItem[];
}

// A custom / "Additional Items" uniform row is only a real line item when it
// has an IDENTITY (a typed name — or a SKU, for School Locker rows keyed by
// SKU) AND a quantity AND a cost. A row with a cost/qty but no name is NOT a
// valid line item: it must never count toward a total or appear on an order.
// (Nameless priced rows caused a total-vs-display mismatch — the total counted
// them, the read view hid them — fixed 2026-07.) This is the single source of
// truth for that rule; use it everywhere custom rows are totalled or listed.
export function customUniformRowCounts(d: UniformLineItem | null | undefined): boolean {
  if (!d) return false;
  const hasName = !!(d.name && String(d.name).trim());
  const hasSku = !!(d.sku && String(d.sku).trim());
  const qty = (d.qty as number) || 0;
  const cost = parseFloat(d.cost as string) || 0;
  return (hasName || hasSku) && qty > 0 && cost > 0;
}

// ── PURE HELPER FUNCTIONS ────────────────────────────────────────────────

export function isGoogleMapsReady(): boolean {
  return !!window.google?.maps?.places;
}

export function isPrimary(yr: string): boolean {
  return PRIMARY_YEARS.includes(yr);
}

export function isValidAUPhone(p: string): boolean {
  const c = p.replace(/[\s\-().]/g, "");
  return /^(0[2-9]\d{8}|04\d{8})$/.test(c);
}

export function deviceCost(costs: StandardCosts, deviceType: string): number {
  const map: Record<string, number> = {
    Laptop: costs.laptop,
    iPad: costs.ipad,
  };
  return map[deviceType] || 0;
}

export function stationeryCost(costs: StandardCosts, yearLevel: string): number {
  return isPrimary(yearLevel) ? costs.stationery_primary : costs.stationery_secondary;
}

// ── UNIFORM HELPER FUNCTIONS ─────────────────────────────────────────────

export function emptyUniformChild() {
  const items: Record<string, { cost: string; qty: number }> = {};
  [...UNIFORM_ITEMS, ...WINTER_ITEMS].forEach((k) => {
    items[k] = { cost: "", qty: 0 };
  });
  return {
    gender: "",
    yearLevel: "",
    schoolName: "",
    isColdClimate: false,
    other: "",
    supplier: "",
    isReimbursement: false,
    items,
  };
}

export function emptySchoolLockerChild() {
  const items: Record<string, { sku: string; size: string; cost: string; qty: number }> = {};
  [...UNIFORM_ITEMS, ...WINTER_ITEMS].forEach((k) => {
    items[k] = { sku: "", size: "", cost: "", qty: 0 };
  });
  const customItems = [
    { name: "", sku: "", size: "", cost: "", qty: 0 },
    { name: "", sku: "", size: "", cost: "", qty: 0 },
    { name: "", sku: "", size: "", cost: "", qty: 0 },
    { name: "", sku: "", size: "", cost: "", qty: 0 },
    { name: "", sku: "", size: "", cost: "", qty: 0 },
  ];
  return {
    gender: "",
    yearLevel: "",
    schoolName: "",
    mobile: "",
    email: "",
    isColdClimate: false,
    items,
    customItems,
  };
}

export function fillSLKeys(child: UniformLike) {
  const items: Record<string, unknown> = Object.assign({}, child.items || {});
  [...UNIFORM_ITEMS, ...WINTER_ITEMS].forEach(function (k) {
    if (!items[k]) items[k] = { sku: "", size: "", cost: "", qty: 0 };
  });
  const existingCustom = child.customItems || [];
  const customItems: unknown[] = [];
  for (let ci = 0; ci < 5; ci++) {
    customItems.push(
      existingCustom[ci]
        ? Object.assign({ name: "", sku: "", size: "", cost: "", qty: 0 }, existingCustom[ci])
        : { name: "", sku: "", size: "", cost: "", qty: 0 }
    );
  }
  return Object.assign({}, child, { items: items, customItems: customItems });
}

export function fillUniKeys(child: UniformLike) {
  const items: Record<string, unknown> = Object.assign({}, child.items || {});
  [...UNIFORM_ITEMS, ...WINTER_ITEMS].forEach(function (k) {
    if (!items[k]) items[k] = { cost: "", qty: 0 };
  });
  return Object.assign({}, child, { items: items });
}

export function calcUniItems(arr: UniformLike[] | null | undefined): number {
  return (arr || []).reduce(function (s, c) {
    const standardTotal = [...UNIFORM_ITEMS, ...WINTER_ITEMS].reduce(function (t, k) {
      const item = c.items?.[k];
      return (
        t + ((item ? item.qty : 0) as number) * (parseFloat((item ? item.cost : 0) as string) || 0)
      );
    }, 0);
    const customTotal = (c.customItems || []).reduce(function (t, d) {
      if (!customUniformRowCounts(d)) return t;
      return t + ((d.qty || 0) as number) * (parseFloat(d.cost as string) || 0);
    }, 0);
    return s + standardTotal + customTotal;
  }, 0);
}
